import { useMemo, useState } from "react";
import {
  api_private_post,
  formatStringAmountCredits,
  showAlert,
} from "script.js";
import "./OrderForm.css";

const CashOutModal = ({ order, onClose, onSuccess }) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [cashOutValue, setCashOutValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cashOutPercent, setCashOutPercent] = useState("100");
  const [activeAmountButton, setActiveAmountButton] = useState(100);
  const [cashOutAmount, setCashOutAmount] = useState(
    Number(order.remaining_amount ?? order.order_amount ?? 0),
  );

  const orderId = order.order_id.toUpperCase().substring(0, 8);
  const cashOutAmountNumber = Number.isFinite(Number(cashOutAmount))
    ? Number(cashOutAmount)
    : 0;
  const cashOutValueNumber = Number.isFinite(Number(cashOutValue))
    ? Number(cashOutValue)
    : 0;

  const remainingAmount = useMemo(() => {
    const amount = Number(order.remaining_amount ?? order.order_amount ?? 0);
    return Number.isFinite(amount) && amount > 0 ? amount : 0;
  }, [order.remaining_amount, order.order_amount]);

  const calculateRequestedAmount = (percent) => {
    const normalizedPercent = Math.max(0, Math.min(Number(percent) || 0, 100));
    return (
      Math.round(((remainingAmount * normalizedPercent) / 100) * 100) / 100
    );
  };

  const handleAmountChange = (event) => {
    setIsConfirming(false);
    setCashOutValue(null);
    setActiveAmountButton(null);

    const percent = event.target.value;
    setCashOutPercent(percent);
    setCashOutAmount(calculateRequestedAmount(percent));
  };

  const handleConfirmCashOut = () => {
    setLoading(true);
    // Execute the cash out
    api_private_post(
      "/account/cash_out_order",
      { order_id: order.order_id, amount: cashOutAmount },
      () => {
        setLoading(false);
        if (onSuccess) onSuccess();
        onClose();
        // Show alert after modal closes to ensure alert element exists
        setTimeout(() => {
          showAlert(
            `Successfully cashed out Holding #${orderId} for ${formatStringAmountCredits(cashOutValue.toFixed(2))}`,
            "success",
          );
        }, 100);
      },
      () => {
        setLoading(false);
        onClose();
        // Show alert after modal closes
        setTimeout(() => {
          showAlert("Failed to cash out holding", "danger");
        }, 100);
      },
    );
  };

  const handleAmountButtonClick = (percent) => {
    setIsConfirming(false);
    setLoading(false);
    setActiveAmountButton(percent);
    setCashOutPercent(percent.toString());
    setCashOutAmount(calculateRequestedAmount(percent));
  };

  const handleMaxButtonClick = () => handleAmountButtonClick(100);

  return (
    <>
      {/* Backdrop */}
      <div className="order-form-overlay active" onClick={onClose}></div>

      {/* Modal */}
      <div className="order-form-panel open">
        <div className="order-form-header">
          <h2 className="order-form-title">Cash Out</h2>
          <button className="order-form-close" onClick={onClose}>
            <i className="ti ti-x"></i>
          </button>
        </div>

        <div className="order-form-content">
          {/* Market Info */}
          <div className="order-market-info">
            <div className="order-market-header">
              <div className="order-market-symbol">
                <div className="order-market-name">
                  <div className="order-market-name-main">
                    {order.product_id.replace(/-/g, " ")}
                  </div>
                  <div className="order-market-name-sub">
                    Holding #{orderId}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="order-summary">
            <div className="order-summary-row">
              <span className="order-summary-label">Original Stake</span>
              <span className="order-summary-value">
                {formatStringAmountCredits(order.order_amount)}
              </span>
            </div>
            <div className="order-summary-row">
              <span className="order-summary-label">Remaining Stake</span>
              <span className="order-summary-value">
                {formatStringAmountCredits(remainingAmount.toFixed(2))}
              </span>
            </div>
            <div className="order-summary-row">
              <span className="order-summary-label">Original Odds</span>
              <span className="order-summary-value">x{order.order_odds}</span>
            </div>
            {cashOutValue !== null && (
              <>
                <div className="order-summary-divider"></div>
                <div className="order-summary-row order-summary-total">
                  <span className="order-summary-label">Cash Out Value</span>
                  <span className="order-summary-total-value">
                    {formatStringAmountCredits(cashOutValueNumber.toFixed(2))}
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="fixed_footer__content-formarea px-0">
            <div className="fs-five fw-bold mb-2">Cash-out stake:</div>
            <div
              className="d-flex align-items-center justify-content-between"
              style={{ marginBottom: 12 }}
            >
              <span></span>
              <span className="fs-nine">
                Maximum:{" "}
                <b className="cpoint" onClick={handleMaxButtonClick}>
                  {formatStringAmountCredits(remainingAmount.toFixed(2))}
                </b>
              </span>
            </div>
            <div className="border-four d-flex align-items-center justify-content-between pe-2 rounded-3 mb-1">
              <input
                className="place-style fw-bold"
                type="text"
                value={formatStringAmountCredits(
                  cashOutAmountNumber.toFixed(2),
                )}
                readOnly
              />
              <button
                className="cmn-btn p-1 fs-seven fw-normal"
                type="button"
                onClick={handleMaxButtonClick}
              >
                Max
              </button>
            </div>
            <div className="range-slider">
              <input
                className="range-slider__range"
                type="range"
                value={cashOutPercent}
                min="0"
                max="100"
                onChange={handleAmountChange}
              />
            </div>
            <div className="fixed_footer__content-selectammount d-flex align-items-center justify-content-between mb-4">
              <span
                onClick={() => handleAmountButtonClick(25)}
                className={`fs-seven cpoint py-1 px-4 border-four rounded-2 clickable-active ${activeAmountButton === 25 ? "active" : ""}`}
              >
                25%
              </span>
              <span
                onClick={() => handleAmountButtonClick(50)}
                className={`fs-seven cpoint py-1 px-4 border-four rounded-2 clickable-active ${activeAmountButton === 50 ? "active" : ""}`}
              >
                50%
              </span>
              <span
                onClick={() => handleAmountButtonClick(75)}
                className={`fs-seven cpoint py-1 px-4 border-four rounded-2 clickable-active ${activeAmountButton === 75 ? "active" : ""}`}
              >
                75%
              </span>
              <span
                onClick={() => handleAmountButtonClick(100)}
                className={`fs-seven cpoint py-1 px-4 border-four rounded-2 clickable-active ${activeAmountButton === 100 ? "active" : ""}`}
              >
                100%
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="order-actions">
            {isConfirming ? (
              <>
                <button
                  className="order-action-btn order-confirm-btn"
                  onClick={handleConfirmCashOut}
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Confirm Cash Out"}
                </button>
                <button
                  className="order-action-btn order-cancel-btn"
                  onClick={() => setIsConfirming(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                className="order-action-btn order-confirm-btn"
                onClick={() => {
                  if (cashOutAmountNumber <= 0) return;

                  setLoading(true);
                  api_private_post(
                    "/account/cash_out_quote",
                    { order_id: order.order_id, amount: cashOutAmountNumber },
                    (response) => {
                      setCashOutValue(response.cash_out_value);
                      setIsConfirming(true);
                      setLoading(false);
                    },
                    () => {
                      setLoading(false);
                      setIsConfirming(false);
                      setCashOutValue(null);
                    },
                  );
                }}
                disabled={loading || cashOutAmountNumber <= 0}
              >
                {loading ? "Loading..." : "Get Cash Out Price"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CashOutModal;
